import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { AlertCircle, ChevronDown, X } from 'lucide-react';
import  { timeRanges } from '../../utils/helperFunctions';
import { DevicesContext } from '../../contexts/DevicesContext';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DeviceDashboardDto, DeviceReading } from '../../services/deviceApi';
import { getDifferenceInHours, getTimezoneAbbreviation, getUserTimezone } from '../../utils/timezoneUtils';
import '../../styles/pages/StatisticalReport.css';
import { UserSelectedContext } from '../../contexts/UserSelectedContext';
import { fillMissingIntervals, FillBounds } from '../../utils/chartDataUtils';

const StatisticalReport: React.FC = () => {
  const isFetchingRef = useRef(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [innerWidth, setInnerWidth] = useState(window.innerWidth);
  const [IsSecondDeviceParam1HasData, setIsSecondDeviceParam1HasData] = useState(true);
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
  const [showTimeRangeDropdown, setShowTimeRangeDropdown] = useState(false);
  const [customReportsDateChanged, setCustomReportsDateChanged] = useState(false);
  const [deviceIds, setDeviceIds] = useState<number[]>([]);
  // const [userSelectedParameterId, setSelectedParameterId] = useState<number>(0);
  // const [userSelectedParameterId2, setUserSelectedParameterId2] = useState<number>(0);
  const [firstDeviceChartData, setFirstDeviceChartData] = useState<any[]>([]);
  const [secondDeviceChartData, setSecondDeviceChartData] = useState<any[]>([]);
  const [customReportsDateError, setCustomReportsDateError] = useState('');
  const [searchTermInDeviceSelection, setSearchTermInDeviceSelection] = useState<string | null>(null);
  const [filteredDevices, setFilteredDevices] = useState<DeviceDashboardDto[]>([])
  const userPreferences = JSON.parse(
    localStorage.getItem('userPreferences') || '{}'
  );
  // const aqiStandardId = userPreferences.aqiStandardId || 1;
  const timeZone = userPreferences.timezone || getUserTimezone();
  // Devices Context
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('StatisticalReport must be used within a DevicesProvider');
  }

  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  /* Use effect hook for getting theme and also it called themehandler when data theme attribute changes */
  useEffect(() => {
    const themeHadler = () => {
      const theme = document.documentElement.getAttribute("data-theme") as 'light' | 'dark' ?? null;
      if(theme) setTheme(theme);
    }

    themeHadler();

    const observer = new MutationObserver(themeHadler);

    observer.observe(document.documentElement,{
      attributes: true,
      attributeFilter: ["data-theme"]
    });

    return () => observer.disconnect();
  }, [])
  

  const {
    devicesForDashboard,
    parametersForDashboard,
    getDevicesForDashboard,
    getParametersForDashboard,
    statisticalReportData,
    getStatisticalReportData,
    loading,
  } = devicesContext;

  // User Selected Context
  const userSelectedContext = useContext(UserSelectedContext);
  if (!userSelectedContext) {
    throw new Error('useUserSelected must be used within a UserSelectedProvider');
  }
  const {
    userSelectedReportsTimeRange,
    setUserSelectedReportsTimeRange,
    userSelectedParameterId,
    setUserSelectedParameterId,
    userSelectedParameterId2,
    setUserSelectedParameterId2,
    customFromDate,
    customToDate,
    setCustomFromDate,
    setCustomToDate,
  } = userSelectedContext;
  
  // Mobile detection
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      setInnerWidth(window.innerWidth);
    };
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.statistical-dropdown-container')) {
        setShowDeviceDropdown(false);
      }
      if (!target.closest('.dropdown-container')) {
        setShowTimeRangeDropdown(false);
      }
    };

    if(isMobile) {
      document.removeEventListener('mousedown', handleClickOutside);
      return;
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  // Get the selected parameter id
  useEffect(() => {
    if (!parametersForDashboard || parametersForDashboard.length === 0) return;
    parametersForDashboard.forEach(parameter => {
      if (parameter.parameterName === 'AQI' && userSelectedParameterId === 0) {
        setUserSelectedParameterId(parameter.driverID);
      }
    });
  }, [parametersForDashboard]);

  // Get devices and parameters if not already fetched
  useEffect(() => {
    if (devicesForDashboard.length === 0) {
      getDevicesForDashboard();
    }
    if (parametersForDashboard.length === 0) {
      getParametersForDashboard();
    }
  }, []);

  /* Filter devices based on search term in device filter */
  useEffect(() => {
    if (searchTermInDeviceSelection != null) {
      setFilteredDevices(devicesForDashboard.filter(d => d.deviceName?.toLocaleLowerCase().includes(searchTermInDeviceSelection.toLocaleLowerCase())));
    } else {
      setFilteredDevices(devicesForDashboard);
    }
  }, [searchTermInDeviceSelection])

    //set device ids
    useEffect(() => {
      if (devicesForDashboard.length > 0) {
        const activeDevices = devicesForDashboard.filter(device => device.isActive === true);
        if (activeDevices.length > 0) {
          setDeviceIds(activeDevices.slice(0, 2).map(device => device.deviceId));
        } else {
          setDeviceIds(devicesForDashboard.slice(0, 2).map(device => device.deviceId));
        }
      }
    }, [devicesForDashboard]);

  // API call when component mounts or time range changes
  useEffect(() => {
    if (!getStatisticalReportData) return;

    if (userSelectedReportsTimeRange === '12h') return;
    if (deviceIds.length === 0) return;

    // Prevent duplicate API calls by using a ref
    if (isFetchingRef.current) return;

    setFirstDeviceChartData([]);
    setSecondDeviceChartData([]);
    isFetchingRef.current = true;

    // Calculate date range based on selected time range
    let fromDate: string;
    let toDate: string = new Date().toISOString();

    if (userSelectedReportsTimeRange === '12h') {
      fromDate = new Date(
        new Date().setHours(new Date().getHours() - 12)
      ).toISOString();
    } else if (userSelectedReportsTimeRange === '24h') {
      fromDate = new Date(
        new Date().setDate(new Date().getDate() - 1)
      ).toISOString();
    } else if (userSelectedReportsTimeRange === '7d') {
      fromDate = new Date(
        new Date().setDate(new Date().getDate() - 7)
      ).toISOString();
    } else if (userSelectedReportsTimeRange === '30d') {
      fromDate = new Date(
        new Date().setDate(new Date().getDate() - 30)
      ).toISOString();
    } else if (userSelectedReportsTimeRange === 'custom') {
      fromDate = new Date(customFromDate).toISOString();
      toDate = new Date(customToDate).toISOString();
    } else {
      fromDate = new Date(
        new Date().setHours(new Date().getHours() - 12)
      ).toISOString();
    }

    getStatisticalReportData({
      deviceIds: deviceIds,
      fromDate: fromDate,
      toDate: toDate,
    }).finally(() => {
      isFetchingRef.current = false;
    });

    // Mark initial load as complet
  }, [userSelectedReportsTimeRange, deviceIds, customReportsDateChanged]);

  // Live data updates ONLY when user is on 12h tab (skip initial load)
  useEffect(() => {
    if (!getStatisticalReportData) return;

    if (userSelectedReportsTimeRange !== '12h') return;
    if (deviceIds.length === 0) return;

    // Prevent duplicate API calls by using a ref
    if (isFetchingRef.current) return;

    setFirstDeviceChartData([]);
    setSecondDeviceChartData([]);
    isFetchingRef.current = true;

    getStatisticalReportData({
      deviceIds: deviceIds,
      fromDate: new Date(
        new Date().setHours(new Date().getHours() - 12)
      ).toISOString(),
      toDate: new Date().toISOString(),
    }).finally(() => {
      isFetchingRef.current = false;
    });
  }, [devicesForDashboard, userSelectedReportsTimeRange, deviceIds]);

  const firstDeviceRecordedDates = {
    firstRecordedAt: statisticalReportData[0]?.readings[0].recordedAt,
    lastRecordedAt: statisticalReportData[0]?.readings[statisticalReportData[0].readings.length - 1].recordedAt,
  }

  const secondDeviceRecordedDates = {
    firstRecordedAt: statisticalReportData[1]?.readings[0].recordedAt,
    lastRecordedAt: statisticalReportData[1]?.readings[statisticalReportData[1].readings.length - 1].recordedAt,
  }

  // Union of both devices' recorded ranges, so every series is filled against the
  // same grid and the two devices' charts share an aligned x-axis.
  const getUnionBounds = (): FillBounds => {
    let startTime: number;
    let endTime: number;
    // Setting start time based on devices first recorded at value
    if (firstDeviceRecordedDates.firstRecordedAt && secondDeviceRecordedDates.firstRecordedAt && firstDeviceRecordedDates.firstRecordedAt <= secondDeviceRecordedDates.firstRecordedAt) {
      startTime = new Date(firstDeviceRecordedDates.firstRecordedAt).getTime()
    } else if (secondDeviceRecordedDates.firstRecordedAt) {
      startTime = new Date(secondDeviceRecordedDates.firstRecordedAt).getTime()
    } else {
      startTime = new Date(firstDeviceRecordedDates.firstRecordedAt).getTime()
    }

    // Setting end time based on devices last recorded at value
    if (firstDeviceRecordedDates.lastRecordedAt && secondDeviceRecordedDates.lastRecordedAt && firstDeviceRecordedDates.lastRecordedAt <= secondDeviceRecordedDates.lastRecordedAt) {
      endTime = new Date(secondDeviceRecordedDates.lastRecordedAt).getTime()
    } else if(secondDeviceRecordedDates.lastRecordedAt) {
      endTime = new Date(secondDeviceRecordedDates.lastRecordedAt).getTime()
    } else {
      endTime = new Date(firstDeviceRecordedDates.lastRecordedAt).getTime()
    }

    return { startTime, endTime };
  };

  // Get the first device chart data
  useEffect(() => {
    if (!statisticalReportData || statisticalReportData.length === 0) return;

    setFirstDeviceChartData([]);
    let baseData: DeviceReading[] = [];
    const deviceData = statisticalReportData.find(d => d.deviceId === deviceIds[0]);
    if (!deviceData) return;

    if (userSelectedParameterId !== 0) {
      baseData = deviceData.readings.filter(
        d => d.parameterDriverId === userSelectedParameterId
      );
    }

    let interval: number = 5 * 60 * 1000;
    if (userSelectedReportsTimeRange === '12h') {
      interval = 5 * 60 * 1000;
    } else if (userSelectedReportsTimeRange === '24h') {
      interval = 1 * 60 * 60 * 1000;
    } else if (
      userSelectedReportsTimeRange === '7d' ||
      userSelectedReportsTimeRange === '30d' ||
      userSelectedReportsTimeRange === 'custom'
    ) {
      interval = 1 * 24 * 60 * 60 * 1000;
    }

    const bounds = getUnionBounds();

    // Single parameter selected
    if (userSelectedParameterId2 === 0) {
      setFirstDeviceChartData(baseData.length !== 0 ? fillMissingIntervals(baseData, interval, bounds) : []);
      return;
    }

    const secondData = deviceData.readings.filter(
      d => d.parameterDriverId === userSelectedParameterId2
    );

    if (baseData.length === 0 && secondData.length === 0) {
      setFirstDeviceChartData([]);
      return;
    } else if (baseData.length !== 0 && secondData.length === 0) {
      setFirstDeviceChartData(fillMissingIntervals(baseData, interval, bounds));
      return;
    } else if (baseData.length === 0 && secondData.length !== 0) {
      setFirstDeviceChartData(fillMissingIntervals(secondData, interval, bounds));
      return;
    }

    // Both parameters have data: fill against the same bounds, merge by index.
    const filledBase = fillMissingIntervals(baseData, interval, bounds);
    const filledSecond = fillMissingIntervals(secondData, interval, bounds);
    const merged = filledBase.map((d, i) => {
      const match = filledSecond[i];
      return {
        ...d,
        parameterValue2: match?.parameterValue ?? null,
        parameterName2: match?.parameterName ?? null,
        parameterDisplayName2: match?.parameterDisplayName ?? null,
        preferredUnitName2: match?.preferredUnitName ?? null,
        subIndex2: match?.subIndex ?? null,
      };
    });
    setFirstDeviceChartData(merged);
  }, [
    userSelectedParameterId,
    userSelectedParameterId2,
    statisticalReportData,
    userSelectedReportsTimeRange,
  ]);

  // Get the second device chart data
  useEffect(() => {
    if (!statisticalReportData || statisticalReportData.length === 0) return;

    setSecondDeviceChartData([]);
    let baseData: DeviceReading[] = [];
    
    const deviceData = statisticalReportData.find(d => d.deviceId === deviceIds[1]);
    if (!deviceData) return;

    if (userSelectedParameterId !== 0) {
      baseData = deviceData.readings.filter(
        d => d.parameterDriverId === userSelectedParameterId
      );
    }

    let interval: number = 5 * 60 * 1000;
    if (userSelectedReportsTimeRange === '12h') {
      interval = 5 * 60 * 1000;
    } else if (userSelectedReportsTimeRange === '24h') {
      interval = 1 * 60 * 60 * 1000;
    } else if (
      userSelectedReportsTimeRange === '7d' ||
      userSelectedReportsTimeRange === '30d' ||
      userSelectedReportsTimeRange === 'custom'
    ) {
      interval = 1 * 24 * 60 * 60 * 1000;
    }


    const bounds = getUnionBounds();

    // Single parameter selected
    if (userSelectedParameterId2 === 0) {
      setSecondDeviceChartData(baseData.length !== 0 ? fillMissingIntervals(baseData, interval, bounds) : []);
      return;
    }

    const secondData = deviceData.readings.filter(
      d => d.parameterDriverId === userSelectedParameterId2
    );

    if (baseData.length === 0 && secondData.length === 0) {
      setSecondDeviceChartData([]);
      return;
    } else if (baseData.length !== 0 && secondData.length === 0) {
      setSecondDeviceChartData(fillMissingIntervals(baseData, interval, bounds));
      !IsSecondDeviceParam1HasData && setIsSecondDeviceParam1HasData(true);
      return;
    } else if (baseData.length === 0 && secondData.length !== 0) {
      setSecondDeviceChartData(fillMissingIntervals(secondData, interval, bounds));
      setIsSecondDeviceParam1HasData(false);
      return;
    }

    // Both parameters have data: fill against the same bounds, merge by index.
    const filledBase = fillMissingIntervals(baseData, interval, bounds);
    const filledSecond = fillMissingIntervals(secondData, interval, bounds);
    const merged = filledBase.map((d, i) => {
      const match = filledSecond[i];
      return {
        ...d,
        parameterValue2: match?.parameterValue ?? null,
        parameterName2: match?.parameterName ?? null,
        parameterDisplayName2: match?.parameterDisplayName ?? null,
        preferredUnitName2: match?.preferredUnitName ?? null,
        subIndex2: match?.subIndex ?? null,
      };
    });
    setSecondDeviceChartData(merged);
    !IsSecondDeviceParam1HasData && setIsSecondDeviceParam1HasData(true);
  }, [
    userSelectedParameterId,
    userSelectedParameterId2,
    statisticalReportData,
    userSelectedReportsTimeRange,
  ]);

  // Handle Time Range Change
  const handleTimeRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserSelectedReportsTimeRange(e.target.value);
    if(e.target.value !== "custom") {
      setShowTimeRangeDropdown(false);
    }
  };

  // Handle parameter click
  const handleParameterClick = (parameterId: number) => {
    // Case 1: clicked the first param again → deselect
    if (userSelectedParameterId === parameterId && userSelectedParameterId2 !== 0) {
      setUserSelectedParameterId(userSelectedParameterId2);
      setUserSelectedParameterId2(0);
      return;
    }

    // Case 2: clicked the second param again → deselect second only
    if (userSelectedParameterId2 === parameterId) {
      setUserSelectedParameterId2(0);
      return;
    }

    // Case 3: no second param selected → set as second
    if (parameterId !== userSelectedParameterId && userSelectedParameterId2 === 0) {
      setUserSelectedParameterId2(parameterId);
      return;
    }

    // Case 4: both already selected → replace second with new
    if (
      parameterId !== userSelectedParameterId &&
      parameterId !== userSelectedParameterId2
    ) {
      setUserSelectedParameterId(userSelectedParameterId2);
      setUserSelectedParameterId2(parameterId);
      return;
    }
  };

  // Get the main pollutant name for the first device
  const getMainPollutantNameForFirstDevice = (recordedAt: string) => {
    const readings = statisticalReportData[0]?.readings?.filter(
      d => new Date(d.recordedAt).toISOString() === recordedAt
    );
    if (!readings || readings.length === 0) return '';
    const maxItem = readings.reduce((prev, current) => current.subIndex > prev.subIndex ? current : prev);
    return maxItem?.parameterName || '';
  };

  // Get the main pollutant name for the second device
  const getMainPollutantNameForSecondDevice = (recordedAt: string) => {
    const readings = statisticalReportData[1]?.readings?.filter(
      d => new Date(d.recordedAt).toISOString() === recordedAt
    );
    if (!readings || readings.length === 0) return '';
    const maxItem = readings.reduce((prev, current) => current.subIndex > prev.subIndex ? current : prev);
    return maxItem?.parameterName || '';
  };

  // Get the parameter name
  const GetParameterName = (parameterId: number) => {
    return parametersForDashboard.find(p => p.driverID === parameterId)?.parameterName || '';
  };

  // Get the parameter name
  const GetParameterUnitName = (parameterId: number) => {
    return parametersForDashboard.find(p => p.driverID === parameterId)?.unit || '';
  };

  // Get the parameter color to show in the tooltip
  // const GetParameterColor = (
  //   value: number,
  //   subIndex: number,
  //   parameterName: string
  //   ) => {
  //     if (
  //       parameterName === 'PM2.5' ||
  //       parameterName === 'PM10' ||
  //       parameterName === 'O₃' ||
  //       parameterName === 'CO' ||
  //       parameterName === 'NO₂'
  //     ) {
  //       if (subIndex === null) {
  //         return HelperFunctions.GetParameterColor(value, parameterName);
  //       } else {
  //         return HelperFunctions.GetParameterColor(subIndex, 'AQI');
  //       }
  //     } else {
  //       return HelperFunctions.GetParameterColor(value, parameterName);
  //     }
  //   };

  // Prepare Highcharts options
  const chartOptions = useMemo((): Highcharts.Options => {
    // if (!firstDeviceChartData || firstDeviceChartData.length === 0 || !firstDeviceChartData[0]) {
    //   return {
    //     title: { text: '' },
    //     series: [],
    //   };
    // }

    /* Get device names */
    const firstDeviceName = devicesForDashboard.find(d => d.deviceId === deviceIds[0])?.deviceName;
    const secondDeviceName = devicesForDashboard.find(d => d.deviceId === deviceIds[1])?.deviceName;

    // 🟢 Added: calculate Y range and detect if all Y values are identical
    const yValues = firstDeviceChartData.map(d => d.parameterValue).filter(v => v != null);
    const yValues2 = secondDeviceChartData.map(d => d.parameterValue).filter(v => v != null);
    const yMin = Math.min(...yValues, ...yValues2);
    const yMax = Math.max(...yValues, ...yValues2);
    const allSame = yMin === yMax;

    const seriesArray: Highcharts.SeriesOptionsType[] = [
      {
        type: 'line',
        name: `${firstDeviceName}-${GetParameterName(userSelectedParameterId)}`,
        data: firstDeviceChartData.map(d => ({
          x: new Date(d.recordedAt).getTime(),
          y: d.parameterValue,
          color: '#005EE5',
        })),
        color: '#005EE5',
        colorByPoint: false,
        lineWidth: 3,
        marker: {
          symbol: 'circle',
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 8,
              lineWidth: 1.5,
              lineColor: '#fff',
            },
          },
        },
        animation: {
          duration: 1500,
        },
        yAxis: 0,
        connectNulls: true,
        zones: undefined,
      } as Highcharts.SeriesLineOptions,
    ];

    if (userSelectedParameterId2 !== 0) {
      let series2Data: Highcharts.SeriesLineOptions['data'] = [];
      if (firstDeviceChartData[0]?.parameterValue2 !== undefined) {
        series2Data = firstDeviceChartData.map(d => ({
          x: new Date(d.recordedAt).getTime(),
          y: d.parameterValue2,
          color: '#E67E7D',
        }));
      }
      seriesArray.push({
        type: 'line',
        name: `${firstDeviceName}-${GetParameterName(userSelectedParameterId2)}`,
        data: series2Data,
        color: '#E67E7D',
        lineWidth: 3,
        marker: {
          symbol: 'square',
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 8,
              lineWidth: 1.5,
              lineColor: '#fff',
            },
          },
        },
        yAxis: 1,
        connectNulls: true,
        animation: {
          duration: 1500,
        },
      } as Highcharts.SeriesLineOptions);
    }

    if (deviceIds.length > 1) {
      let series3Data: Highcharts.SeriesLineOptions['data'] = [];
      if (secondDeviceChartData.length > 0) {
        series3Data = secondDeviceChartData.map(d => ({
        x: new Date(d.recordedAt).getTime(),
        y: d.parameterValue,
        color: '#EEC94D',
        }));
      }
      seriesArray.push({
        type: 'line',
        name: `${secondDeviceName}-${GetParameterName(userSelectedParameterId)}`,
        data: series3Data,
        color: '#EEC94D',
        lineWidth: 3,
        marker: {
          symbol: 'circle',
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 8,
              lineWidth: 1.5,
              lineColor: '#fff',
            },
          },
        },
        yAxis: IsSecondDeviceParam1HasData ? 0 : 1,
        connectNulls: true,
        animation: {
          duration: 1500,
        },
      } as Highcharts.SeriesLineOptions);


      if (userSelectedParameterId2 !== 0) {
        let series4Data: Highcharts.SeriesLineOptions['data'] = [];
        if (secondDeviceChartData[0]?.parameterValue2 !== undefined) {
          series4Data = secondDeviceChartData.map(d => ({
          x: new Date(d.recordedAt).getTime(),
            y: d.parameterValue2,
            color: '#00A5A8',
          }));
        }

        seriesArray.push({
          type: 'line',
          name: `${secondDeviceName}-${GetParameterName(userSelectedParameterId2)}`,
          data: series4Data,
          color: '#00A5A8',
          lineWidth: 3,
          marker: {
            symbol: 'square',
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 8,
                lineWidth: 1.5,
                lineColor: '#fff',
              },
            },
          },
          yAxis: 1,
          connectNulls: true,
          animation: {
            duration: 1500,
          },
        } as Highcharts.SeriesLineOptions);
      }
    }

    return {
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        animation: true,
        style: {
          fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
        },
        spacingLeft: 5,
        spacingRight: 5,
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        type: 'datetime',
        lineWidth: 1,
        tickWidth: 1,
        lineColor: theme == 'light' ? '#E9EBF1' : '#4C5C68',
        tickColor: theme == 'light' ? '#E9EBF1' : '#4C5C68',
        crosshair: {
          width: 1,
          color: '#9291A5',
          dashStyle: 'Dash',
        },
        labels: {
          style: {
            color: theme == 'light' ? '#363636' : '#ffffff',
            fontSize: '12px',
            fontWeight: '400',
            fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
          },
          formatter: function () {
            const date = new Date(this.value);
            if (
              userSelectedReportsTimeRange === '12h' ||
              userSelectedReportsTimeRange === '24h'
            ) {
              return date.toLocaleTimeString('en-US', {
                timeZone: timeZone,
                hour: '2-digit',
                minute: '2-digit',
              });
            } else if (userSelectedReportsTimeRange === '7d') {
              return date.toLocaleDateString('en-US', {
                timeZone: timeZone,
                month: 'short',
                day: 'numeric',
              });
            } else if (userSelectedReportsTimeRange === '30d') {
              return date.toLocaleDateString('en-US', {
                timeZone: timeZone,
                month: 'short',
                day: 'numeric',
              });
            } else if (userSelectedReportsTimeRange === 'custom') {
              const diffHours = getDifferenceInHours(new Date(customFromDate), new Date(customToDate));
              if (diffHours <= 12 || diffHours <= 24) {
                return date.toLocaleTimeString('en-US', {
                  timeZone: timeZone,
                  hour: '2-digit',
                  minute: '2-digit',
                });
              }else {
                return date.toLocaleDateString('en-US', {
                  timeZone: timeZone,
                  month: 'short',
                  day: 'numeric',
                });
              }
            }
            return '';
          },
        },
        gridLineWidth: 0,
      },
      yAxis: [
        {
          title: {
            // Use compact parameter name (e.g., SO₂) instead of full display name
            text: `${GetParameterName(userSelectedParameterId)}${firstDeviceChartData[0]?.preferredUnitName || secondDeviceChartData[0]?.preferredUnitName ? ` (${GetParameterUnitName(userSelectedParameterId)})` : ''}`,
            style: {
              color: theme == 'light' ? '#4C5C68' : '#ffffff',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: '400',
              fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
            },
          },
          labels: {
            style: {
              color: theme == 'light' ? '#363636' : '#ffffff',
              fontSize: isMobile ? '10px' : '12px',
              fontWeight: '400',
              fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
            },
          },
          gridLineColor: '#E9EBF1',
          lineWidth: 1,
          lineColor: theme == 'light' ? '#E9EBF1' : '#4C5C68',
          tickWidth: 1,
          tickColor: theme == 'light' ? '#E9EBF1' : '#4C5C68',
          softMin: allSame ? (yMin < 1 ? yMin - yMin : yMin - 1) : undefined,
          softMax: allSame ? yMax + 1 : undefined,
        },
        ...(userSelectedParameterId2 !== 0 
          ? [
              {
                title: {
                  // Use compact parameter name for second axis as well
                  text: `${GetParameterName(userSelectedParameterId2)}${firstDeviceChartData[0]?.preferredUnitName2 || secondDeviceChartData[0]?.preferredUnitName2 ? ` (${GetParameterUnitName(userSelectedParameterId2)})` : ''}`,
                  style: {
                    color: theme == 'light' ? '#4C5C68' : '#ffffff',
                    fontSize: isMobile ? '10px' : '12px',
                    fontWeight: '300',
                    fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
                  },
                },
                labels: {
                  style: {
                    color: theme == 'light' ? '#363636' : '#ffffff',
                    fontSize: isMobile ? '10px' : '12px',
                    fontWeight: '500',
                    fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
                  },
                },
                opposite: true,
                gridLineWidth: 0,
                lineWidth: 1,
                lineColor: theme == 'light' ? '#E9EBF1' : '#4C5C68',
                tickWidth: 1,
                tickColor: theme == 'light' ? '#E9EBF1' : '#4C5C68',
              },
            ]
          : []),
      ],
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: 'transparent',
        borderWidth: 0,
        shadow: false,
        padding: 0,
        style: {
          pointerEvents: 'none',
        },
        positioner: function (labelWidth, labelHeight, point) {
          const chart = this.chart;
          const plotLeft = chart.plotLeft;
          const plotTop = chart.plotTop;
          const plotWidth = chart.plotWidth;
          const plotHeight = chart.plotHeight;
          let x = point.plotX + plotLeft + 10;
          let y = point.plotY + plotTop - labelHeight / 2;
          if (x + labelWidth > plotLeft + plotWidth) {
            x = point.plotX + plotLeft - labelWidth - 10;
          }
          if (x < plotLeft) { x = plotLeft; }
          if (y < plotTop) { y = plotTop; }
          if (y + labelHeight > plotTop + plotHeight) {
            y = plotTop + plotHeight - labelHeight;
          }
          return { x, y };
        },
        formatter: function () {
          const points = this.points;
          if (!points || points.length === 0) return '';

          const point = points[0];
          const firstDevicedata = (point as any).custom || firstDeviceChartData[(point as any).index];
          const secondDevicedata = (point as any).custom || secondDeviceChartData[(point as any).index];

          const formatValue = (val: any, paramName: string) => {
            if (val == null) return 'N/A';
            if (paramName === 'AQI') {
              return Math.round(val);
            }
            return Number.isInteger(val)
              ? val
              : val < 0.01
                ? val.toFixed(3)
                : val.toFixed(2);
          };

          const bgColor = theme === 'light' ? '#fff' : '#333333';
          const textColor = theme === 'light' ? '#333' : '#f0f0f0';
          const subTextColor = theme === 'light' ? '#6b7280' : '#9ca3af';

          const tooltipDate = new Date(firstDevicedata?.recordedAt || secondDevicedata?.recordedAt);
          const timePart = tooltipDate.toLocaleTimeString('en-US', { timeZone: timeZone, hour: '2-digit', minute: '2-digit', hour12: true });
          const datePart = tooltipDate.toLocaleDateString('en-US', { timeZone: timeZone, year: 'numeric', month: 'short', day: 'numeric' });
          const tzAbbr = getTimezoneAbbreviation(timeZone);

          let tooltipHTML = `<div style="font-size:11px; min-width:160px; max-width:320px; line-height:1.5; white-space:normal; word-wrap:break-word; overflow-wrap:break-word; font-family:'DM Sans','Inter',system-ui,sans-serif; background:${bgColor}; color:${textColor}; padding:${isMobile ? '8px' : '12px'}; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.15);">`;

          tooltipHTML += `<div style="margin-bottom:4px; font-weight:600; white-space:nowrap;">${timePart} ${tzAbbr}, ${datePart}</div>`;

          // First device tooltip details
          tooltipHTML += `<div style="margin-bottom:4px; font-size: ${isMobile ? '12px' : '14px'}; font-weight: 400; color: ${textColor};">${firstDeviceName}</div>`;

          tooltipHTML += `<div style="color: #005EE5; margin-bottom:4px; font-size: ${isMobile ? '12px' : '14px'}; font-weight: 500; margin-left: 14px; white-space:nowrap;"><span style="display: inline-block;box-shadow: 0px 4px 14px 0px #00000040;width: 10px; height: 10px;background-color: #005EE5; border-radius: 50%; margin-right: 4px;"></span>${GetParameterName(userSelectedParameterId)}: ${formatValue(firstDevicedata?.parameterValue, firstDevicedata?.parameterName || '')} ${GetParameterUnitName(userSelectedParameterId)}</div>`;

          if (userSelectedParameterId2 !== 0) {
            tooltipHTML += `<div style="color: #E67E7D; margin-bottom:4px; font-size: ${isMobile ? '12px' : '14px'}; font-weight: 500; margin-left: 14px; white-space:nowrap;"><span style="display: inline-block;box-shadow: 0px 4px 14px 0px #00000040;width: 10px; height: 10px;background-color: #E67E7D; margin-right: 4px;"></span>${GetParameterName(userSelectedParameterId2)}: ${formatValue(firstDevicedata?.parameterValue2, firstDevicedata?.parameterName2 || '')} ${GetParameterUnitName(userSelectedParameterId2)}</div>`;
          }

          if (GetParameterName(userSelectedParameterId) === 'AQI' || GetParameterName(userSelectedParameterId2) === 'AQI') {
            const mainPollutant = getMainPollutantNameForFirstDevice(firstDevicedata?.recordedAt || '');
            if (mainPollutant) {
              tooltipHTML += `<div style="margin-bottom:4px; font-size: ${isMobile ? '12px' : '14px'}; font-weight: 300; color: ${subTextColor}; margin-left: 14px;">Main pollutant: ${mainPollutant}</div>`;
            }
          }

          if (deviceIds.length > 1) {
            // Second device tooltip details
            tooltipHTML += `<div style="margin-bottom:4px; font-size: ${isMobile ? '12px' : '14px'}; font-weight: 400; color: ${textColor};">${secondDeviceName}</div>`;

            tooltipHTML += `<div style="color: #EEC94D; margin-bottom:4px; font-size: ${isMobile ? '12px' : '14px'}; font-weight: 500; margin-left: 14px; white-space:nowrap;"><span style="display: inline-block;box-shadow: 0px 4px 14px 0px #00000040;width: 10px; height: 10px;background-color: #EEC94D; border-radius: 50%; margin-right: 4px;"></span>${GetParameterName(userSelectedParameterId)}: ${formatValue(secondDevicedata?.parameterValue, secondDevicedata?.parameterName || '')} ${GetParameterUnitName(userSelectedParameterId)}</div>`;

            if (userSelectedParameterId2 !== 0) {
              tooltipHTML += `<div style="color: #00A5A8; margin-bottom:4px; font-size: ${isMobile ? '12px' : '14px'}; font-weight: 500; margin-left: 14px; white-space:nowrap;"><span style="display: inline-block;box-shadow: 0px 4px 14px 0px #00000040;width: 10px; height: 10px;background-color: #00A5A8; margin-right: 4px;"></span>${GetParameterName(userSelectedParameterId2)}: ${formatValue(secondDevicedata?.parameterValue2, secondDevicedata?.parameterName2 || '')} ${GetParameterUnitName(userSelectedParameterId2)}</div>`;
            }

            if (GetParameterName(userSelectedParameterId) === 'AQI' || GetParameterName(userSelectedParameterId2) === 'AQI') {
              const mainPollutant = getMainPollutantNameForSecondDevice(secondDevicedata?.recordedAt || '');
              if (mainPollutant) {
                tooltipHTML += `<div style="margin-bottom:4px; font-size: ${isMobile ? '12px' : '14px'}; font-weight: 300; color: ${subTextColor}; margin-left: 14px;">Main pollutant: ${mainPollutant}</div>`;
              }
            }
          }
          tooltipHTML += `</div>`;
          return tooltipHTML;
        },
      },
      legend: {
        enabled: true,
        verticalAlign: 'bottom',
        symbolWidth: 0,
        useHTML: true,
        events: {
          itemClick: function () {
            return false; // Prevent item click
          },
        },
        labelFormatter: function() {
          return `
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="width: 10px; height: 10px;background-color: ${this.color}; border-radius: ${this.index % 2 === 0 ? '50%' : '0px'};"></span>
              ${this.name}
            </div>
          `;
        },
        itemStyle: {
          color: theme == 'light' ? '#363636' : '#ffffff',
          fontSize: '12px',
          fontWeight: '400',
          fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
        },
        itemHoverStyle: {
          fontWeight: '500',
        },
      },
      plotOptions: {
        line: {
          animation: true,
          marker: {
            enabled: false,
          },
        },
        series: {
          stickyTracking: true,
          states: {
            hover: {
              lineWidthPlus: 0,
            },
          },
        },
      },
      series: seriesArray,
    };
  }, [
    firstDeviceChartData,
    secondDeviceChartData,
    userSelectedParameterId,
    userSelectedParameterId2,
    deviceIds,
    theme,
    isMobile,
    timeZone,
    userSelectedReportsTimeRange,
    customFromDate,
    customToDate,
    devicesForDashboard,
    parametersForDashboard,
    IsSecondDeviceParam1HasData,
  ]);

  // Handle device change
  const handleDeviceChange = (e: React.ChangeEvent<HTMLInputElement>, deviceId: number) => {
    e.preventDefault();
    const checked = e.target.checked;
    if (checked) {
      if (deviceIds.length > 1) {
        const newDeviceId = deviceIds.pop() as number;
        setDeviceIds([newDeviceId, deviceId]);
      } else {
        setDeviceIds([...deviceIds, deviceId]);
      }
    } else {
      if (deviceIds.length > 1) {
        setDeviceIds(deviceIds.filter(id => id !== deviceId));
      }
    }
    // setShowDeviceDropdown(false);
  };

  // Hadnle device un selection

  const handleDeviceUnSelection = (deviceId: number) => {
    if (deviceIds.length > 1) {
      setDeviceIds(deviceIds.filter(id => id !== deviceId));
      setSearchTermInDeviceSelection(null);
    }
  }

  /* Handle custom reports date change */
  const handleCustomReportsDateChange = () => {
    if(!customFromDate || !customToDate) {
      setCustomReportsDateError('Please select From and To dates');
      return;
    }
    
    // Extract date part (YYYY-MM-DD) and compare dates only, ignoring time
    // Handle both "YYYY-MM-DD" and "YYYY-MM-DDTHH:MM" formats
    const fromDateStr = customFromDate.includes('T') ? customFromDate.split('T')[0] : customFromDate;
    const toDateStr = customToDate.includes('T') ? customToDate.split('T')[0] : customToDate;
    
    // Compare date strings directly (YYYY-MM-DD format allows string comparison)
    if(fromDateStr >= toDateStr) {
      setCustomReportsDateError('Start date must be before end date');
      return;
    }
    setCustomReportsDateError('');
    setCustomReportsDateChanged(!customReportsDateChanged);
    setShowTimeRangeDropdown(false);
  };

  //Cancel custom date selection
  const handleCustomReportsDateCancel = () => {
    setCustomReportsDateError('');
    setShowTimeRangeDropdown(false);
  };

  // Check the data is availeble for slelected parameters for any device 
  // const checkIsChartDataAvaiilable = () => {
  //   if ((firstDeviceChartData.length > 0 || secondDeviceChartData.length > 0) && firstDeviceChartData.some(c => c.parameterValue != null || c.parameterValue2 != null) || secondDeviceChartData.some(c => c.parameterValue != null || c.parameterValue2 != null)) {
  //     return true;
  //   }

  //   return false;
  // }

  return (
    <>
      <div className="space-y-5 overflow-y-auto md:overflow-y-hidden flex flex-col flex-1">
        {/* Header Section */}
        {!isMobile && (
          <div className="zoomOutLevel">
            <h2 className="device-title">Pollutant Trend Comparison Across Devices</h2>
            <p className="device-subtitle ml-0">
            Compare pollutant trends between devices over time.
            </p>
          </div>
        )}

        <div className="statistical-report-container zoomOutLevel">
          <div className="statistical-report-header">
            {/* Chart Title */}
            <div className="statistical-report-chart-title-section">
              
            </div>
            {/* Device and Time Range Dropdowns */}
            <div
              className={
                !isMobile
                  ? 'flex items-center gap-6 flex-1'
                  : 'w-full grid grid-cols-2 gap-4 px-4'
              }
            >
              {/* Device Filter */}
              <div className="flex items-center justify-end gap-2 flex-1">
                <label className="text-base sm:block hidden font-normal text-[var(--color-text)]">
                  Device:
                </label>
                {isMobile ? (
                  <button
                    onClick={() => {
                      if(devicesForDashboard.length === 0) {
                        return;
                      }
                      setShowDeviceDropdown(true);
                    }}
                    className="flex items-center gap-2 w-full device-dropdown-select"
                  >
                    <span>
                      {devicesForDashboard.find(
                        device => device.deviceId === deviceIds[0]
                      )?.deviceName || 'Select Device'}
                      {deviceIds.length > 1 && `, ${devicesForDashboard.find(
                        device => device.deviceId === deviceIds[1]
                      )?.deviceName}`}
                    </span>
                    <ChevronDown className="w-5 h-5 ml-auto" />
                  </button>
                ) : (
                  <div className="statistical-dropdown-container">
                    <div
                      className="statistical-dropdown-label"
                      onClick={() => {
                        if(devicesForDashboard.length === 0) {
                          return;
                        }
                        setShowDeviceDropdown(!showDeviceDropdown);
                        setShowTimeRangeDropdown(false);
                      }}
                    >
                      <div className="statistical-dropdown-label-content">
                        <p>
                          {devicesForDashboard.find(
                            device => device.deviceId === deviceIds[0]
                          )?.deviceName || 'Select Device'}
                          {deviceIds.length > 1 && `, ${devicesForDashboard.find(
                            device => device.deviceId === deviceIds[1]
                          )?.deviceName}`}
                        </p>
                      </div>
                      <ChevronDown className="w-5 h-5 text-[var(--color-text)]" />
                    </div>
                    {showDeviceDropdown && (
                      <div className="statistical-dropdown-options">
                        <div className='statistical-dropdown-header'>
                          <span>{devicesForDashboard.find(device => device.deviceId === deviceIds[0])?.deviceName || 'Select Device'} <X onClick = {() => handleDeviceUnSelection(deviceIds[0])} strokeWidth={1.5} className='statistical-dropdown-cross-icon' /> </span>
                          {deviceIds.length > 1 && <span>{devicesForDashboard.find(device => device.deviceId === deviceIds[1])?.deviceName} <X onClick = {() => handleDeviceUnSelection(deviceIds[1])} strokeWidth={1.5} className='statistical-dropdown-cross-icon' /></span>}
                        </div>
                        {/* Search for devices */}
                        <div className='device-search'>
                          <input type="text" id="search" placeholder='Search by device name...' className='device-search-input' onChange={(e)=> setSearchTermInDeviceSelection(e.target.value)} />
                        </div>
                          {filteredDevices.length > 0 ? (
                            filteredDevices.map(device => (
                              <label
                                key={device.deviceId}
                                className="statistical-dropdown-option"
                              >
                                <input
                                  type="checkbox"
                                  name="device"
                                  value={device.deviceId}
                                  checked={deviceIds.includes(device.deviceId)}
                                  onChange={(e) =>
                                    handleDeviceChange(e, device.deviceId)
                                  }
                                  className="checkbox-input"
                                />
                                <span className="checkbox-label">
                                  {device.deviceName}
                                  <span className="checkbox-label-location">({device.installationLocation})</span>
                                </span>
                              </label>
                            ))
                          ) : (
                            <div className='statistical-dropdown-option'>No devices found</div>
                          )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Time Range Dropdown */}
              {isMobile ? (
                <button
                  onClick={() => setShowTimeRangeDropdown(true)}
                  className="flex gap-2 items-center device-dropdown-select"
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/SVGIcons/CalenderIcon.svg`}
                    alt="calendar"
                    className="w-4 h-4"
                  />
                  <span>
                    {timeRanges.find(
                      range => range.value === userSelectedReportsTimeRange
                    )?.label || 'Select Time Range'}
                  </span>
                  <ChevronDown className="w-5 h-5 ml-auto" />
                </button>
              ) : (
                <div className="dropdown-container">
                  <div
                    className="dropdown-label"
                    onClick={() => {
                      setShowTimeRangeDropdown(!showTimeRangeDropdown);
                      setShowDeviceDropdown(false);
                    }}
                  >
                    <div className="dropdown-label-content">
                      <img
                        src={`${process.env.PUBLIC_URL}/SVGIcons/CalenderIcon.svg`}
                        alt="arrow"
                        className="mr-2"
                      />
                      <p>
                        {
                          timeRanges.find(
                            range =>
                              range.value === userSelectedReportsTimeRange
                          )?.label
                        }
                      </p>
                    </div>
                    <ChevronDown className="w-5 h-5 text-[var(--color-text)]" />
                  </div>
                  {showTimeRangeDropdown && (
                    <div className='dropdown-options'>
                      {timeRanges.map(range => (
                        <label key={range.value} className="dropdown-option">
                          <input
                            type="radio"
                            name="timeRange"
                            value={range.value}
                            checked={
                              userSelectedReportsTimeRange === range.value
                            }
                            onChange={handleTimeRangeChange}
                            className="radio-input"
                          />
                          <span className="radio-label">{range.label}</span>
                        </label>
                      ))}

                      {userSelectedReportsTimeRange === "custom" && (
                        <div className="custom-time-range-container">
                          <div className="custom-time-range-inputs">
                            <div className="custom-date-field-wrapper">
                              <label htmlFor="customFromDate" className="custom-date-label">From:</label>
                              <input
                                type="date"
                                name="timeRange"
                                id="customFromDate"
                                value={customFromDate.split('T')[0]}
                                onClick={(e) => e.currentTarget.showPicker()}
                                onChange={(e) => setCustomFromDate(e.target.value)}
                                className="custom-date-input"
                              />
                            </div>
                            <div className="custom-date-field-wrapper">
                              <label htmlFor="customToDate" className="custom-date-label">To:</label>
                              <input
                                type="date"
                                name="timeRange"
                                id="customToDate"
                                value={customToDate.split('T')[0]}
                                onClick={(e) => e.currentTarget.showPicker()}
                                onChange={(e) => setCustomToDate(e.target.value)}
                                className="custom-date-input"
                              />
                            </div>
                          </div>
                          {customReportsDateError && (
                            <div className="custom-date-error-container">
                              <AlertCircle className="w-4 h-4 text-red-500" />
                              <p>{customReportsDateError}</p>
                            </div>
                          )}
                          <div className="custom-date-buttons">
                            <button onClick={handleCustomReportsDateCancel} className="custom-date-cancel-button">Cancel</button>
                            <button onClick={handleCustomReportsDateChange} className="custom-date-ok-button">Apply</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {loading ? (
            <div className="loading-container statistical-report-chart-container">
              <div className="h-8 w-8 border-b-2 border-[var(--gradient-button-left-color)] mb-4 animate-spin rounded-full"></div>
              <p className="text-base text-[var(--color-sub-title-text)]">
                Loading statistical report data...
              </p>
            </div>
          ) : (
            <>
              {statisticalReportData.length > 0 ? (
                <div className="statistical-report-chart-container" key={innerWidth}>
                  {firstDeviceChartData.length > 0 || secondDeviceChartData.length > 0 ? (
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={chartOptions}
                      containerProps={{
                        style: { flex: '1 1 0%' },
                      }}
                    />
                  ) : (
                    <div className="error-container" style={{ height: '90%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem' }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '1rem' }}>
                        <path d="M3 3v18h18" />
                        <path d="M7 16l4-4 4 4 5-5" />
                      </svg>
                      <p style={{ fontSize: '1rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>No Data Available</p>
                      <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>There is no recorded data for the selected time period. Please try selecting a different date range.</p>
                    </div>
                  )}

                  {/* Metric parameter selectors*/}
                  <div className="metric-selectors px-4 mb-1">
                    {parametersForDashboard.length > 0 &&
                      parametersForDashboard
                        .sort((a, _b) => (a.parameterName === 'AQI' ? -1 : 1))
                        .map(parameter => (
                          <button
                            key={parameter.driverID}
                            className={`metric-button ${userSelectedParameterId === parameter.driverID || userSelectedParameterId2 === parameter.driverID ? 'active' : ''}`}
                            onClick={() =>
                              handleParameterClick(parameter.driverID)
                            }
                          >
                            {parameter.parameterName === 'Temperature'
                                ? 'Temp'
                                : parameter.parameterName === 'Humidity'
                                  ? 'Hum'
                                  : parameter.parameterName}
                          </button>
                        ))}
                  </div>
                </div>
              ) : (
                <div className="error-container statistical-report-chart-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '1rem' }}>
                    <path d="M3 3v18h18" />
                    <path d="M7 16l4-4 4 4 5-5" />
                  </svg>
                  <p style={{ fontSize: '1rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>No Data Available</p>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>There is no recorded data for the selected time period. Please try selecting a different date range.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile Device Selection Modal */}
      {isMobile && showDeviceDropdown && (
        <div
          className="mobile-modal-overlay"
          onClick={() => setShowDeviceDropdown(false)}
        >
          <div className="mobile-modal" onClick={e => e.stopPropagation()}>
            <div className="mobile-modal-header">
              <h3 className="mobile-modal-title">Select Device</h3>
              <X
                className="w-5 h-5 text-[var(--color-text)]"
                onClick={() => setShowDeviceDropdown(false)}
              />
            </div>
            {/* Search for devices */}
            <div className='device-search'>
              <input type="text" id="search" placeholder='Search by device name...' className='device-search-input' onChange={(e)=> setSearchTermInDeviceSelection(e.target.value)} />
            </div>
            <div className="mobile-modal-content">
              {filteredDevices.length > 0 ? (
                filteredDevices.map(device => (
                  <label key={device.deviceId} className="mobile-modal-option">
                    <input
                      type="checkbox"
                      name="device"
                      value={device.deviceId}
                      checked={deviceIds.includes(device.deviceId)}
                      onChange={e => handleDeviceChange(e, device.deviceId)}
                      className="mobile-modal-checkbox checkbox-input"
                    />
                    <span className="mobile-modal-label">
                      {device.deviceName}
                      <span className="checkbox-label-location">({device.installationLocation})</span>
                    </span>
                  </label>
                ))
              ) : (
                <div className='statistical-dropdown-option'>No devices found</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Time Range Selection Modal */}
      {isMobile && showTimeRangeDropdown && (
        <div
          className="mobile-modal-overlay"
          onClick={() => setShowTimeRangeDropdown(false)}
        >
          <div className="mobile-modal" onClick={e => e.stopPropagation()}>
            <div className="mobile-modal-header">
              <h3 className="mobile-modal-title">Select Time Range</h3>
              <X
                className="w-5 h-5 text-[var(--color-text)]"
                onClick={() => setShowTimeRangeDropdown(false)}
              />
            </div>
            <div className="mobile-modal-content">
              {timeRanges.map(range => (
                <label key={range.value} className="mobile-modal-option">
                  <input
                    type="radio"
                    name="timeRange"
                    value={range.value}
                    checked={userSelectedReportsTimeRange === range.value}
                    onChange={handleTimeRangeChange}
                    className="mobile-modal-radio"
                  />
                  <span className="mobile-modal-label">{range.label}</span>
                </label>
              ))}
              {userSelectedReportsTimeRange === "custom" && (
                <div className="custom-time-range-container">
                  <div className="custom-time-range-inputs">
                    <div className="custom-date-field-wrapper">
                      <label htmlFor="customFromDate" className="custom-date-label">From:</label>
                      <input
                        type="date"
                        name="timeRange"
                        id="customFromDate"
                        value={customFromDate.split('T')[0]}
                        onClick={(e) => e.currentTarget.showPicker()}
                        onChange={(e) => setCustomFromDate(e.target.value)}
                        className="custom-date-input"
                      />
                    </div>
                    <div className="custom-date-field-wrapper">
                      <label htmlFor="customToDate" className="custom-date-label">To:</label>
                      <input
                        type="date"
                        name="timeRange"
                        id="customToDate"
                        value={customToDate.split('T')[0]}
                        onClick={(e) => e.currentTarget.showPicker()}
                        onChange={(e) => setCustomToDate(e.target.value)}
                        className="custom-date-input"
                      />
                    </div>
                  </div>
                  {customReportsDateError && (
                    <div className="custom-date-error-container">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      <p>{customReportsDateError}</p>
                    </div>
                  )}
                  <div className="custom-date-buttons">
                    <button onClick={handleCustomReportsDateCancel} className="custom-date-cancel-button">Cancel</button>
                    <button onClick={handleCustomReportsDateChange} className="custom-date-ok-button">Apply</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StatisticalReport;
